import type { AppDispatch, RootState } from 'tradera/state/configure-store';
import feesActions from './fees-actions';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import { resetCharityAndShippingInfo } from 'tradera/apps/syi/state/actions/charity-actions';
import { PURE_BIN_CATEGORY_IDS, ITEM_TYPE } from '../constants';
import { selectIsFeatureEnabled } from 'tradera/state/environment/selectors';
import { setCustomEndTime } from '../reducers/item-duration';
import { selectMagicalSyiSettngEnabled } from 'tradera/apps/syi/state/selectors/draft-selectors';
import { selectIsPureBinSelected } from 'tradera/apps/syi/state/selectors/price-selectors';
import { setHasUsedAutoFill } from 'tradera/apps/syi/state/reducers/predictions-reducer';
import {
    setAuctionBinPrice,
    setFixedPrice,
    setOpeningBid,
    setReservePrice as setReservePriceReducer,
    setReservePriceVisibility as setReservePriceVisibilityReducer,
    setCharityDonationVisibility as setCharityDonationVisibilityReducer,
    setIsPureBinSelected as setIsPureBinSelectedReducer,
    setVat,
    setSelectedItemType as setSelectedItemTypeReducer,
    setReservePriceCanChangeButNotBeRemoved,
} from 'tradera/apps/syi/state/reducers/price';
import { isNullOrUndefined } from 'tradera/utils/condition-check';

export const setReservePrice =
    (reservePrice: number | null) =>
    (dispatch: AppDispatch): void => {
        dispatch(setReservePriceReducer(reservePrice));
        dispatch(feesActions.updatePrices());
    };

export const setReservePriceVisibility =
    (visible: boolean) =>
    (dispatch: AppDispatch): void => {
        dispatch(setReservePriceVisibilityReducer(visible));
        if (!visible) {
            dispatch(setReservePrice(null));
        }
    };

export const setCharityDonationVisibility =
    (visible: boolean) =>
    (dispatch: AppDispatch): void => {
        dispatch(setCharityDonationVisibilityReducer(visible));
        if (!visible) {
            dispatch(resetCharityAndShippingInfo());
        }
    };

export const setIsPureBinSelected =
    (isPureBinSelected: boolean) =>
    (dispatch: AppDispatch): void => {
        dispatch(setIsPureBinSelectedReducer(isPureBinSelected));

        if (isPureBinSelected) {
            dispatch(setOpeningBid(null));
            dispatch(setAuctionBinPrice(null));
            dispatch(setReservePrice(null));
            dispatch(setCustomEndTime(null));
        } else {
            dispatch(setFixedPrice(null));
        }
    };

export const setIsPureBinSelectedFromPrediction =
    (isPureBinSelected: boolean) =>
    (dispatch: AppDispatch, getState: () => RootState): void => {
        const isPristine = !getSyiSlice(getState()).price.isDirty;
        if (!isPristine) return;

        const oldValue = selectIsPureBinSelected(getState());

        dispatch(setIsPureBinSelected(isPureBinSelected));

        if (oldValue !== isPureBinSelected) {
            dispatch(setHasUsedAutoFill(true));
        }
    };

export const setSelectedItemType =
    (itemType: (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE]) =>
    (dispatch: AppDispatch): void => {
        dispatch(setSelectedItemTypeReducer(itemType));

        if (itemType === ITEM_TYPE.BIN) {
            dispatch(setOpeningBid(null));
            dispatch(setAuctionBinPrice(null));
            dispatch(setReservePrice(null));
            dispatch(setCustomEndTime(null));
        } else if (itemType === ITEM_TYPE.AUCTION) {
            dispatch(setFixedPrice(null));
            dispatch(setAuctionBinPrice(null));
        } else if (itemType === ITEM_TYPE.AUCTION_AND_BIN) {
            dispatch(setFixedPrice(null));
        }
    };

export const setFromDraft =
    (
        openingBid: number | null,
        fixedPrice: number | null,
        reservePrice: number | null,
        charityId: string | null,
        vat: number | null,
        isChangeItem: boolean,
    ) =>
    (dispatch: AppDispatch): void => {
        const queryParams = new URLSearchParams(location.search);
        const queryParamCharityId = queryParams.get('charityId');
        const hasOpeningBid = !isNullOrUndefined(openingBid);
        const hasFixedPrice = !isNullOrUndefined(fixedPrice);
        const hasReservePrice = !isNullOrUndefined(reservePrice);
        const hasCharityId =
            !isNullOrUndefined(charityId) ||
            !isNullOrUndefined(queryParamCharityId);
        const isAuction = hasOpeningBid && !hasFixedPrice;
        const isAuctionBin = hasOpeningBid && hasFixedPrice;
        const isPureBin = !hasOpeningBid && hasFixedPrice;

        dispatch(setVat(vat));

        // TODO: fewer changes to the state makes the page load faster...
        if (isAuction) {
            dispatch(setOpeningBid(openingBid));
            dispatch(setReservePrice(reservePrice));
            dispatch(setAuctionBinPrice(null));
            dispatch(setFixedPrice(null));
            dispatch(setReservePriceVisibility(hasReservePrice));
            dispatch(setCharityDonationVisibility(hasCharityId));
            dispatch(setIsPureBinSelected(false));
            dispatch(setSelectedItemType(ITEM_TYPE.AUCTION));
        } else if (isAuctionBin) {
            dispatch(setOpeningBid(openingBid));
            dispatch(setReservePrice(reservePrice));
            dispatch(setAuctionBinPrice(fixedPrice));
            dispatch(setFixedPrice(null));
            dispatch(setReservePriceVisibility(hasReservePrice));
            dispatch(setCharityDonationVisibility(hasCharityId));
            dispatch(setIsPureBinSelected(false));
            dispatch(setSelectedItemType(ITEM_TYPE.AUCTION_AND_BIN));
        } else if (isPureBin) {
            dispatch(setOpeningBid(null));
            dispatch(setReservePrice(null));
            dispatch(setAuctionBinPrice(null));
            dispatch(setFixedPrice(fixedPrice));
            dispatch(setReservePriceVisibility(false));
            dispatch(setCharityDonationVisibility(false));
            dispatch(setIsPureBinSelected(true));
            dispatch(setSelectedItemType(ITEM_TYPE.BIN));
        } else {
            dispatch(setOpeningBid(null));
            dispatch(setReservePrice(null));
            dispatch(setAuctionBinPrice(null));
            dispatch(setFixedPrice(null));
            dispatch(setReservePriceVisibility(false));
            dispatch(setCharityDonationVisibility(hasCharityId));
            dispatch(setIsPureBinSelected(false));
        }

        dispatch(
            setReservePriceCanChangeButNotBeRemoved(
                isChangeItem && hasReservePrice,
            ),
        );
    };

export const rootCategoryHasBeenSelected =
    (categoryId: number) =>
    (dispatch: AppDispatch, getState: () => RootState): void => {
        const isPureBinCategory = PURE_BIN_CATEGORY_IDS.includes(categoryId);
        const isMagicalSyiFeatureEnabled =
            selectIsFeatureEnabled('magical-syi')(getState());
        const isMagicalSyiSettingEnabled =
            selectMagicalSyiSettngEnabled(getState());
        const isAutoFillConditionAndListingTypeEnabled = selectIsFeatureEnabled(
            'auto-fill-condition-and-listing-type',
        )(getState());
        const isEditOfPublishedItem =
            getSyiSlice(getState()).syi.isEditOfPublishedItem;

        // We do not want to change the auction type if the user is editing a
        // published item, since that's not allowed anyway
        if (
            !isEditOfPublishedItem &&
            !isMagicalSyiFeatureEnabled &&
            !isAutoFillConditionAndListingTypeEnabled &&
            !isMagicalSyiSettingEnabled
        ) {
            dispatch(setIsPureBinSelected(isPureBinCategory));
        }
    };
